import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Loader2, CheckCircle2, AlertCircle, Clock, Landmark, RotateCcw, Ban, History, Leaf, Settings2, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

const TABS = ["Enhancers", "Centers", "Held", "History", "Ecosystem", "Schedule", "Accounts"];

function ConnectedAccounts() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-connect-accounts"],
    queryFn: () => api.get("/admin/connect/accounts?balances=1").then((r) => r.data.accounts),
  });
  if (isLoading) return <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[#144F75]" /></div>;
  const rows = data || [];
  const incomplete = rows.filter((r) => !r.onboarding_complete);
  return (
    <div data-testid="admin-connect-accounts">
      {incomplete.length > 0 && (
        <p className="text-sm text-[#B5794E] mb-3" data-testid="admin-connect-incomplete">
          ⚠ {incomplete.length} account{incomplete.length > 1 ? "s" : ""} with incomplete Stripe onboarding — they can't receive payouts yet.
        </p>
      )}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={`${r.kind}-${r.id}`} className="eyw-card p-4 flex items-center gap-3 flex-wrap" data-testid={`connect-acct-${r.id}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="text-[#1A242B] font-medium">{r.name} <span className="text-[10px] uppercase text-[#8FA89B]">· {r.kind}</span></p>
              <p className="text-xs text-[#8FA89B]">{r.account_id || "No Stripe account yet"}{r.payout_method !== "stripe" ? ` · pays via ${r.payout_method}` : ""}</p>
            </div>
            <span className={`text-[11px] rounded-full px-2.5 py-1 ${r.onboarding_complete ? "bg-[#5C8A72]/15 text-[#5C8A72]" : r.account_id ? "bg-[#E0A458]/20 text-[#B5794E]" : "bg-[#F1EDE6] text-[#8FA89B]"}`}>
              {r.onboarding_complete ? "Payouts enabled" : r.account_id ? "Onboarding incomplete" : "Not connected"}
            </span>
            {r.payout_hold && <span className="text-[11px] rounded-full px-2.5 py-1 bg-red-50 text-red-500">On hold</span>}
            <div className="text-right text-xs text-[#4A5D6B]">
              <p>Owed on platform: <span className="font-medium">{money((r.pending_platform_balance || 0))}</span></p>
              {r.stripe_balance?.available != null && (
                <p>Stripe balance: <span className="font-medium text-[#5C8A72]">{money(r.stripe_balance.available)}</span>
                  {r.stripe_balance.pending > 0 ? ` (+${money(r.stripe_balance.pending)} pending)` : ""}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PayoutHistoryTab() {
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-payout-records", filterStatus, filterType],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterType) params.set("recipient_type", filterType);
      params.set("limit", "100");
      return api.get(`/admin/payout-records?${params}`).then((r) => r.data.records);
    },
  });

  const failedCount = (data || []).filter((r) => r.status === "failed").length;

  return (
    <div data-testid="payout-history-tab">
      {failedCount > 0 && (
        <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3" data-testid="failed-payouts-alert">
          <XCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-red-700">{failedCount} failed payout{failedCount > 1 ? "s" : ""} need attention</p>
            <p className="text-xs text-red-500 mt-0.5">Review below — retry manually or check the partner's Stripe account status.</p>
          </div>
        </div>
      )}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[["", "All"], ["paid", "Paid"], ["failed", "Failed"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilterStatus(v)} data-testid={`history-status-${v || "all"}`}
            className={`px-3 py-1 rounded-full text-xs ${filterStatus === v ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B]"}`}>{l}</button>
        ))}
        <span className="mx-1 text-[#E5E0D8]">|</span>
        {[["", "All types"], ["enhancer", "Enhancers"], ["center", "Centers"], ["ecosystem", "Ecosystem"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilterType(v)} data-testid={`history-type-${v || "all"}`}
            className={`px-3 py-1 rounded-full text-xs ${filterType === v ? "bg-[#D98E73] text-white" : "bg-[#F1EEE8] text-[#4A5D6B]"}`}>{l}</button>
        ))}
      </div>
      {isLoading ? <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
        : (data || []).length === 0 ? <div className="eyw-card p-10 text-center text-[#4A5D6B]" data-testid="history-empty"><History size={26} className="mx-auto text-[#8FA89B] mb-2" /> No payout records yet.</div>
        : (
          <div className="eyw-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EB] text-[#4A5D6B]">
                <tr><th className="text-left px-4 py-3">Recipient</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Amount</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Transfer ID</th><th className="text-left px-4 py-3">Date</th></tr>
              </thead>
              <tbody className="divide-y divide-[#E5E0D8]">
                {(data || []).map((r) => (
                  <tr key={r.id} data-testid={`payout-record-${r.id}`} className={r.status === "failed" ? "bg-red-50" : ""}>
                    <td className="px-4 py-3 font-medium text-[#1A242B]">{r.recipient_name || r.recipient_id}</td>
                    <td className="px-4 py-3 text-[#4A5D6B] capitalize">{r.recipient_type}</td>
                    <td className="px-4 py-3 text-[#144F75] font-semibold">{money(Number(r.amount || 0))}</td>
                    <td className="px-4 py-3">
                      {r.status === "paid"
                        ? <span className="text-[11px] rounded-full px-2.5 py-1 bg-[#5C8A72]/15 text-[#5C8A72] inline-flex items-center gap-1"><CheckCircle2 size={11} /> Paid</span>
                        : <span className="text-[11px] rounded-full px-2.5 py-1 bg-red-100 text-red-600 inline-flex items-center gap-1"><XCircle size={11} /> Failed</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#8FA89B] font-mono">{r.stripe_transfer_id ? r.stripe_transfer_id.slice(0, 20) + "…" : r.failure_reason ? <span className="text-red-500">{r.failure_reason.slice(0, 40)}</span> : "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#8FA89B]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function EcosystemTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-payout-config"],
    queryFn: () => api.get("/admin/payout-config").then((r) => r.data),
  });
  return (
    <div data-testid="ecosystem-tab">
      <div className="mb-4">
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-1"><Leaf size={18} className="text-[#5C8A72]" /> Ecosystem earnings (EYW platform 10% share)</h3>
        <p className="text-sm text-[#4A5D6B]">The platform's 10% ecosystem allocation from every transaction. These funds remain in the EYW Stripe account — no outbound transfer needed.</p>
      </div>
      {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div> : (
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="eyw-card p-5" data-testid="eco-total"><p className="text-[11px] uppercase tracking-wide text-[#8FA89B] mb-1">Total earned (all time)</p><p className="font-serif text-3xl text-[#144F75]">{money((data?.ecosystem_total ?? 0))}</p></div>
          <div className="eyw-card p-5" data-testid="eco-available"><p className="text-[11px] uppercase tracking-wide text-[#5C8A72] mb-1">Released & available</p><p className="font-serif text-3xl text-[#5C8A72]">{money((data?.ecosystem_available ?? 0))}</p></div>
          <div className="eyw-card p-5" data-testid="eco-held"><p className="text-[11px] uppercase tracking-wide text-[#B5794E] mb-1">Held (pending completion)</p><p className="font-serif text-3xl text-[#B5794E]">${(data?.ecosystem_held ?? 0).toFixed(2)}</p></div>
        </div>
      )}
      <div className="eyw-card p-5 bg-[#F9F7F3]">
        <p className="text-sm text-[#4A5D6B]">These funds are collected directly through Stripe checkout and are already in the EYW platform Stripe account. Use the Stripe Dashboard to manage disbursements, payroll, or operating costs from this balance.</p>
      </div>
    </div>
  );
}

function ScheduleTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-payout-config"],
    queryFn: () => api.get("/admin/payout-config").then((r) => r.data),
  });
  const [enhFreq, setEnhFreq] = useState("");
  const [cenFreq, setCenFreq] = useState("");
  const [saving, setSaving] = useState(false);

  const current = data || {};
  const eFreq = enhFreq || current.payout_frequency_enhancer || "weekly";
  const cFreq = cenFreq || current.payout_frequency_center || "weekly";

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/payout-config", {
        payout_frequency_enhancer: eFreq,
        payout_frequency_center: cFreq,
      });
      toast.success("Payout schedule saved.");
      qc.invalidateQueries({ queryKey: ["admin-payout-config"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const freqOpts = ["daily", "weekly", "monthly"];

  return (
    <div data-testid="schedule-tab" className="max-w-lg space-y-6">
      <div>
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-1"><Settings2 size={18} className="text-[#144F75]" /> Payout schedule configuration</h3>
        <p className="text-sm text-[#4A5D6B]">Controls how often the auto-payout runner transfers released balances. Default is weekly.</p>
      </div>
      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" size={20} /> : (
        <>
          <div>
            <label className="block text-sm font-medium text-[#1A242B] mb-2">Enhancer / vendor payout frequency</label>
            <div className="flex gap-2">
              {freqOpts.map((f) => (
                <button key={f} onClick={() => setEnhFreq(f)} data-testid={`enh-freq-${f}`}
                  className={`px-4 py-2 rounded-lg text-sm capitalize ${eFreq === f ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B]"}`}>{f}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A242B] mb-2">Center payout frequency</label>
            <div className="flex gap-2">
              {freqOpts.map((f) => (
                <button key={f} onClick={() => setCenFreq(f)} data-testid={`cen-freq-${f}`}
                  className={`px-4 py-2 rounded-lg text-sm capitalize ${cFreq === f ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B]"}`}>{f}</button>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-payout-schedule" className="eyw-btn !py-2 text-sm">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Settings2 size={14} />} Save schedule
          </button>
        </>
      )}
    </div>
  );
}

export default function AdminPayoutsPanel() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("Enhancers");
  const [payingId, setPayingId] = useState("");

  const enhancers = useQuery({ queryKey: ["admin-payouts"], queryFn: () => api.get("/admin/payouts/pending").then((r) => r.data.payouts) });
  const centers = useQuery({ queryKey: ["admin-payouts-centers"], queryFn: () => api.get("/admin/payouts/centers").then((r) => r.data.centers) });
  const held = useQuery({ queryKey: ["admin-payouts-held"], queryFn: () => api.get("/admin/payouts/held").then((r) => r.data) });

  const payEnhancer = async (row) => {
    if (!window.confirm(`Transfer ${money(row.pending)} to ${row.name} via Stripe? This moves real money.`)) return;
    setPayingId(row.enhancer_id);
    try {
      const { data } = await api.post(`/admin/payouts/${row.enhancer_id}/pay`);
      toast.success(`Paid ${money(data.paid)} to ${row.name} (${data.count} items)`);
      qc.invalidateQueries({ queryKey: ["admin-payouts"] });
      qc.invalidateQueries({ queryKey: ["admin-payout-records"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPayingId(""); }
  };

  const payCenter = async (row) => {
    if (!window.confirm(`Transfer ${money(row.pending)} to ${row.name} via Stripe? This moves real money.`)) return;
    setPayingId(row.location_id);
    try {
      const { data } = await api.post(`/admin/payouts/centers/${row.location_id}/pay`);
      toast.success(`Paid ${money(data.paid)} to ${row.name} (${data.count} items)`);
      qc.invalidateQueries({ queryKey: ["admin-payouts-centers"] });
      qc.invalidateQueries({ queryKey: ["admin-payout-records"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPayingId(""); }
  };

  const clawback = async (row) => {
    const amt = window.prompt(`Clawback amount to recover from ${row.name} (refund/chargeback):`, "");
    if (!amt) return;
    const reason = window.prompt("Reason (optional):", "") || "";
    try {
      const { data } = await api.post(`/admin/payouts/${row.enhancer_id}/clawback`, { amount: Number(amt), reason });
      toast.success(`Clawback of ${money(data.clawback)} recorded; payouts held.`);
      qc.invalidateQueries({ queryKey: ["admin-payouts"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleHold = async (row) => {
    try {
      await api.post(`/admin/payouts/${row.enhancer_id}/hold`, { hold: !row.payout_hold });
      toast.success(row.payout_hold ? "Hold cleared." : "Payouts held.");
      qc.invalidateQueries({ queryKey: ["admin-payouts"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const acct = (r) => r.payouts_enabled
    ? <span className="text-xs text-[#5C8A72] inline-flex items-center gap-1"><CheckCircle2 size={13} /> Ready</span>
    : r.connected ? <span className="text-xs text-[#B5794E] inline-flex items-center gap-1"><AlertCircle size={13} /> Setup incomplete</span>
    : <span className="text-xs text-[#8FA89B]">Not connected</span>;

  return (
    <div data-testid="admin-payouts-panel">
      <div className="mb-5">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Banknote size={22} className="text-[#144F75]" /> Partner payouts</h2>
        <p className="text-sm text-[#4A5D6B] mt-1">Released balances owed to enhancers, vendors and Centers. Held earnings become payable after completion/shipment. The schedule controls when automatic transfers run.</p>
        <button data-testid="admin-run-auto-payouts" className="eyw-btn-ghost !py-1.5 text-xs mt-2"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/payouts/run-auto");
              const n = (data.paid || []).length;
              toast.success(n ? `Auto payout run complete — ${n} transfer${n > 1 ? "s" : ""} sent.` : "Auto payout run complete — nothing due.");
              if ((data.errors || []).length) toast.error(data.errors[0]);
              qc.invalidateQueries();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}>Run auto payouts now</button>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`payouts-tab-${t.toLowerCase()}`}
            className={`px-4 py-1.5 rounded-full text-sm ${tab === t ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B]"}`}>{t}</button>
        ))}
      </div>

      {tab === "Enhancers" && (
        enhancers.isLoading ? <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
        : (enhancers.data || []).length === 0 ? <div className="eyw-card p-10 text-center text-[#4A5D6B]" data-testid="payouts-empty"><CheckCircle2 size={26} className="mx-auto text-[#5C8A72] mb-2" /> No released enhancer/vendor payouts right now.</div>
        : (
          <div className="eyw-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EB] text-[#4A5D6B]"><tr><th className="text-left px-4 py-3">Partner</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Items</th><th className="text-left px-4 py-3">Available</th><th className="text-left px-4 py-3">Account</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-[#E5E0D8]">
                {enhancers.data.map((r) => (
                  <tr key={r.enhancer_id} data-testid={`payout-row-${r.enhancer_id}`} className={r.payout_hold ? "bg-[#B45151]/[0.05]" : ""}>
                    <td className="px-4 py-3"><span className="text-[#1A242B] font-medium">{r.name}</span><br /><span className="text-xs text-[#8FA89B]">{r.email}</span>{r.payout_hold && <span className="ml-1 text-[10px] text-[#B45151]">· HOLD</span>}</td>
                    <td className="px-4 py-3 text-[#4A5D6B]">{r.is_vendor ? "Vendor" : "Enhancer"}</td>
                    <td className="px-4 py-3 text-[#4A5D6B]">{r.count}</td>
                    <td className="px-4 py-3 text-[#144F75] font-semibold">{money(r.pending)}</td>
                    <td className="px-4 py-3">{acct(r)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => clawback(r)} data-testid={`payout-clawback-${r.enhancer_id}`} className="eyw-btn-ghost !py-1.5 !px-2 text-xs mr-1" title="Record clawback"><RotateCcw size={13} /></button>
                      <button onClick={() => toggleHold(r)} data-testid={`payout-hold-${r.enhancer_id}`} className="eyw-btn-ghost !py-1.5 !px-2 text-xs mr-1" title={r.payout_hold ? "Clear hold" : "Hold"}><Ban size={13} className={r.payout_hold ? "text-[#B45151]" : ""} /></button>
                      <button onClick={() => payEnhancer(r)} disabled={!r.payouts_enabled || r.payout_hold || payingId === r.enhancer_id} data-testid={`payout-pay-${r.enhancer_id}`} className="eyw-btn !py-1.5 !px-3 text-xs disabled:opacity-40">{payingId === r.enhancer_id ? <Loader2 className="animate-spin" size={13} /> : <Banknote size={13} />} Pay</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "Centers" && (
        centers.isLoading ? <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
        : (centers.data || []).length === 0 ? <div className="eyw-card p-10 text-center text-[#4A5D6B]" data-testid="centers-empty"><Landmark size={26} className="mx-auto text-[#5C8A72] mb-2" /> No Center location-use payouts pending.</div>
        : (
          <div className="eyw-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EB] text-[#4A5D6B]"><tr><th className="text-left px-4 py-3">Center</th><th className="text-left px-4 py-3">Items</th><th className="text-left px-4 py-3">Available</th><th className="text-left px-4 py-3">Account</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-[#E5E0D8]">
                {centers.data.map((r) => (
                  <tr key={r.location_id} data-testid={`center-payout-row-${r.location_id}`}>
                    <td className="px-4 py-3 text-[#1A242B] font-medium">{r.name}</td>
                    <td className="px-4 py-3 text-[#4A5D6B]">{r.count}</td>
                    <td className="px-4 py-3 text-[#144F75] font-semibold">{money(r.pending)}</td>
                    <td className="px-4 py-3">{r.connected ? <span className="text-xs text-[#5C8A72] inline-flex items-center gap-1"><CheckCircle2 size={13} /> Connected</span> : <span className="text-xs text-[#8FA89B]">Not connected</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => payCenter(r)} disabled={!r.connected || payingId === r.location_id} data-testid={`center-payout-pay-${r.location_id}`} className="eyw-btn !py-1.5 !px-3 text-xs disabled:opacity-40">{payingId === r.location_id ? <Loader2 className="animate-spin" size={13} /> : <Banknote size={13} />} Pay</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "Held" && (
        held.isLoading ? <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
        : (
          <div className="eyw-card p-6" data-testid="held-summary">
            <p className="text-sm text-[#4A5D6B] mb-3 flex items-center gap-2"><Clock size={16} className="text-[#B5794E]" /> Earnings held until completion/shipment (not yet payable).</p>
            <p className="font-serif text-3xl text-[#B5794E] mb-4">{money((held.data?.held_total ?? 0))} <span className="text-sm text-[#8FA89B]">across {held.data?.count ?? 0} entries</span></p>
            <div className="grid sm:grid-cols-3 gap-3">
              {Object.entries(held.data?.by_kind || {}).map(([k, v]) => (
                <div key={k} className="rounded-xl bg-[#F9F7F3] px-4 py-3"><p className="text-xs uppercase tracking-wide text-[#8FA89B]">{k}</p><p className="font-serif text-xl text-[#144F75]">{money(Number(v))}</p></div>
              ))}
            </div>
          </div>
        )
      )}

      {tab === "History" && <PayoutHistoryTab />}
      {tab === "Ecosystem" && <EcosystemTab />}
      {tab === "Schedule" && <ScheduleTab />}
      {tab === "Accounts" && <ConnectedAccounts />}
    </div>
  );
}
